import { useState, useEffect } from "react";
import axios from "axios";
import { Percent, FloppyDisk, ArrowClockwise, Check, ChartLineUp, Bank, Receipt } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommerceManager() {
  const [cfg, setCfg] = useState(null);
  const [pct, setPct] = useState(5);
  const [revenue, setRevenue] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const fetchAll = async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/admin/commerce/config`, { withCredentials: true }),
        axios.get(`${API}/admin/commerce/revenue?limit=50`, { withCredentials: true }),
      ]);
      setCfg(c.data);
      setPct(c.data.commission_percent);
      setRevenue(r.data.items || []);
    } catch (e) {
      setError("Failed to load");
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const save = async () => {
    setError(""); setSaving(true); setSaved(false);
    try {
      const r = await axios.put(`${API}/admin/commerce/config`,
        { commission_percent: Number(pct) }, { withCredentials: true });
      setCfg({ ...cfg, ...r.data });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <p className="text-stone-400 text-sm">Loading…</p>;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="commerce-manager">
      {/* Commission card */}
      <div className="bg-stone-900 border border-stone-800 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center">
            <Percent size={20} weight="fill" className="text-emerald-400" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-lg font-semibold text-white">Platform Commission</h2>
            <p className="text-xs text-stone-400">Percentage of each carbon credit sale retained by Conexus Foundation.</p>
          </div>
        </div>

        <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1">Commission percent (0–50)</label>
        <div className="flex items-center gap-3 mb-4">
          <input
            type="range" min="0" max="50" step="0.5"
            value={pct} onChange={(e) => setPct(Number(e.target.value))}
            className="flex-1 accent-emerald-500"
            data-testid="commerce-pct-range"
          />
          <div className="flex items-center gap-1 bg-stone-800 border border-stone-700 rounded-lg px-3 py-2">
            <input
              type="number" min="0" max="50" step="0.5"
              value={pct}
              onChange={(e) => setPct(Math.min(50, Math.max(0, Number(e.target.value) || 0)))}
              className="w-16 bg-transparent text-white text-sm text-right focus:outline-none"
              data-testid="commerce-pct-input"
            />
            <span className="text-stone-500 text-xs">%</span>
          </div>
        </div>

        <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-xl p-3 mb-4 text-xs text-emerald-200">
          Example: A buyer pays <strong>$100</strong> → platform keeps <strong>${(100 * pct / 100).toFixed(2)}</strong> ·
          seller receives <strong>${(100 * (100 - pct) / 100).toFixed(2)}</strong>.
        </div>

        {error && <p className="text-xs text-red-400 mb-3">{error}</p>}

        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-stone-700 text-white text-sm font-medium px-5 py-2.5 rounded-full"
            data-testid="commerce-save"
          >
            <FloppyDisk size={14} weight="fill" /> {saving ? "Saving…" : "Save Commission"}
          </button>
          <button onClick={fetchAll} className="text-stone-400 hover:text-white text-xs" data-testid="commerce-reload">
            <ArrowClockwise size={14} className="inline" /> Reload
          </button>
          {saved && (
            <span className="text-emerald-400 text-xs inline-flex items-center gap-1" data-testid="commerce-saved">
              <Check size={14} weight="bold" /> Saved
            </span>
          )}
        </div>
      </div>

      {/* Revenue stats */}
      <div className="bg-stone-900 border border-stone-800 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-600/20 flex items-center justify-center">
            <ChartLineUp size={20} weight="fill" className="text-amber-400" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-white">Platform Revenue</h3>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="bg-stone-800/50 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Lifetime</p>
            <p className="font-heading text-2xl font-light text-white">${(cfg.lifetime_revenue || 0).toLocaleString()}</p>
          </div>
          <div className="bg-stone-800/50 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Transactions</p>
            <p className="font-heading text-2xl font-light text-white">{cfg.transactions || 0}</p>
          </div>
        </div>

        <h4 className="text-[10px] uppercase tracking-wider text-stone-500 mb-2 flex items-center gap-2">
          <Receipt size={12} /> Latest commission events
        </h4>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {revenue.length === 0 ? (
            <p className="text-xs text-stone-500 italic">No revenue yet. The platform earns when a carbon credit is sold.</p>
          ) : revenue.map((r) => (
            <div key={r.id} className="flex items-center justify-between text-xs bg-stone-800/30 rounded-lg px-3 py-2" data-testid={`revenue-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-stone-300 truncate font-mono text-[10px]">{r.tx_ref}</p>
                <p className="text-stone-500 text-[10px]">{new Date(r.at).toLocaleString()}</p>
              </div>
              <div className="text-right ml-2">
                <p className="text-emerald-300 font-medium">+${(r.amount || 0).toFixed(2)}</p>
                <p className="text-stone-500 text-[10px]">{r.commission_percent}% of ${(r.gross || 0).toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
